import React, { useEffect, useState } from 'react'
import { Button, Stack, Textarea } from '@chakra-ui/react'

import {
  UpupDashboardsQuery,
  useUpupInsertDashboardMutation,
  useUpupUpdateDashboardMutation
} from 'graphql/generated'
import { BaseFormField } from 'components/ChakraToolkit/FormField'
import { Sidepeek } from 'ui'
import { IconPicker } from './IconPicker'

export type DashboardRecord = UpupDashboardsQuery['dashboards'][number]

type DashboardData = {
  category: string
  name: string
  description: string
  url: string
  icon: string | null
}

const emptyDashboardData: DashboardData = {
  category: '',
  name: '',
  description: '',
  url: '',
  icon: null
}

const DashboardModal = ({
  isOpen,
  onClose,
  dashboard
}: {
  isOpen: boolean
  onClose: () => void
  dashboard?: DashboardRecord
}) => {
  const [dashboardErrors, setDashboardErrors] = useState<Record<string, string>>({})
  const [_, insertDashboard] = useUpupInsertDashboardMutation()
  const [__, updateDashboard] = useUpupUpdateDashboardMutation()
  const [dashboardData, setDashboardData] = useState<DashboardData>(emptyDashboardData)
  const fields: Array<{
    key: keyof DashboardData
    label: string
    required: boolean
  }> = [
    {
      key: 'category',
      label: 'Category',
      required: true
    },
    {
      key: 'name',
      label: 'Name',
      required: true
    },
    {
      key: 'description',
      label: 'Description',
      required: true
    },
    {
      key: 'url',
      label: 'URL',
      required: true
    },
    {
      key: 'icon',
      label: 'Icon',
      required: false
    }
  ]

  useEffect(() => {
    if (!isOpen) {
      return
    }

    if (dashboard) {
      setDashboardData({
        category: dashboard.category,
        name: dashboard.name,
        description: dashboard.description,
        url: dashboard.url,
        icon: dashboard.icon ?? null
      })
    } else {
      setDashboardData(emptyDashboardData)
    }
    setDashboardErrors({})
  }, [isOpen, dashboard?.id])

  const validateDashboardData = (
    data: DashboardData,
    fields: Array<{ key: keyof DashboardData; label: string; required: boolean }>
  ) => {
    const errors: Record<string, string> = {}
    let isValid = true
    fields.forEach(({ key, required }) => {
      const value = data[key]
      if (required && (value === null || value === undefined || value === '')) {
        errors[key as string] = `${key} is required`
        isValid = false
      }
    })
    return { errors, isValid }
  }

  const handleClose = () => {
    setDashboardData(emptyDashboardData)
    setDashboardErrors({})
    onClose()
  }

  const handleSubmit = async () => {
    const { errors, isValid } = validateDashboardData(dashboardData, fields)
    setDashboardErrors(errors)
    if (!isValid) {
      return
    }
    if (dashboard) {
      await updateDashboard({
        id: dashboard.id,
        ...dashboardData
      })
    } else {
      await insertDashboard(dashboardData)
    }
    handleClose()
  }

  return (
    <Sidepeek
      isOpen={isOpen}
      onClose={handleClose}
      title={dashboard ? 'Edit Dashboard' : 'Add a Dashboard'}
      closeOnOverlayClick={true}
    >
      <Stack spacing={4}>
        {fields.map(({ key, label, required }) => (
          <BaseFormField<DashboardData>
            key={key}
            field={key}
            label={label}
            isRequired={required}
            errors={dashboardErrors}
          >
            <>
              {key === 'icon' ? (
                <IconPicker
                  value={dashboardData.icon}
                  onChange={(name) => setDashboardData((current) => ({ ...current, icon: name }))}
                />
              ) : null}
              {key !== 'icon' ? (
                <Textarea
                  value={dashboardData[key] || ''}
                  onChange={(e) => setDashboardData({ ...dashboardData, [key]: e.target.value })}
                  placeholder={`Enter the dashboard ${key}`}
                  rows={1}
                />
              ) : null}
            </>
          </BaseFormField>
        ))}
        <Button onClick={() => handleSubmit()} colorScheme='teal'>
          Submit
        </Button>
      </Stack>
    </Sidepeek>
  )
}

export default DashboardModal
